type MountedSvelteComponent = {
  $destroy?: () => void;
};

type FederatedSvelteComponentConstructor<T extends MountedSvelteComponent> = new (options: {
  target: HTMLElement;
  props?: Record<string, unknown>;
  context?: Map<unknown, unknown>;
}) => T;

export type FederatedSvelteMountOptions = {
  target?: HTMLElement | null;
  props?: Record<string, unknown>;
  context?: Map<unknown, unknown>;
  clearTarget?: boolean;
};

/**
 * Destroys a mounted Svelte component instance if one exists.
 */
export const destroySvelteComponentInstance = <T extends MountedSvelteComponent | null>(
  instance: T,
): null => {
  if (instance && typeof instance.$destroy === "function") {
    instance.$destroy();
  }

  return null;
};

/**
 * Mounts a module-federated Svelte component with an isolated context map.
 * If an older instance exists, it is destroyed before the new one is mounted.
 */
export const mountFederatedSvelteComponent = <T extends MountedSvelteComponent>(
  currentInstance: T | null,
  Component: FederatedSvelteComponentConstructor<T>,
  {
    target = null,
    props = undefined,
    context = new Map(),
    clearTarget = true,
  }: FederatedSvelteMountOptions = {},
): T | null => {
  destroySvelteComponentInstance(currentInstance);

  if (!target) {
    return null;
  }

  if (clearTarget) {
    target.innerHTML = "";
  }

  const mountOptions: {
    target: HTMLElement;
    context: Map<unknown, unknown>;
    props?: Record<string, unknown>;
  } = {
    target,
    context,
  };

  if (props !== undefined) {
    mountOptions.props = props;
  }

  return new Component(mountOptions);
};
